import React, { useState } from 'react';
import {
  Container, Accordion, Table, Badge,
  Card, Button
} from 'react-bootstrap';
import AppHeader from '../../components/Common/AppHeader';
import AppFooter from '../../components/Common/AppFooter';

const taxonomies = [
  {
    name: "India - NEP 2020",
    intent: "Defines hierarchical outcome mapping aligned with CBCS and NEP reforms.",
    levels: ["Course", "Unit", "Chapter"],
    verbs: {
      1: ["identify", "recall"],
      2: ["explain", "describe"],
      3: ["apply", "execute"]
    },
    policy: "CLO–PLO mapping mandatory | CBCS credit system",
    regulator: "UGC (India)"
  },
  {
    name: "EU - ECTS (Bologna)",
    intent: "Standardizes European education via workload-based credit transfer.",
    levels: ["Module", "Study Unit", "Learning Activity"],
    verbs: {
      1: ["recognize", "define"],
      2: ["summarize", "classify"],
      3: ["implement", "demonstrate"]
    },
    policy: "ECTS credit alignment | Workload validation",
    regulator: "European Commission"
  },
  {
    name: "UK - QAA / FHEQ",
    intent: "Ensures academic rigor across UK higher education qualifications.",
    levels: ["Module", "Week", "Session"],
    verbs: {
      1: ["list", "state"],
      2: ["interpret", "discuss"],
      3: ["solve", "use"]
    },
    policy: "FHEQ level mapping | Harvard citation required",
    regulator: "QAA (UK)"
  },
  {
    name: "USA - Carnegie Standard",
    intent: "Defines credit-hour based academic structure in US institutions.",
    levels: ["Course", "Week", "Lesson"],
    verbs: {
      1: ["identify", "label"],
      2: ["paraphrase", "locate"],
      3: ["illustrate", "demonstrate"]
    },
    policy: "Minimum contact hours enforcement",
    regulator: "Carnegie Foundation"
  },
  {
    name: "Brazil - MEC",
    intent: "Regulates curriculum structure and documentation standards in Brazil.",
    levels: ["Disciplina", "Unidade", "Tópico"],
    verbs: {
      1: ["identificar", "listar"],
      2: ["explicar", "descrever"],
      3: ["aplicar", "executar"]
    },
    policy: "Bibliography compliance (ABNT)",
    regulator: "Ministry of Education (Brazil)"
  },
  {
    name: "Asia-Pacific Framework",
    intent: "General competency-based structure used across APAC institutions.",
    levels: ["Subject", "Module", "Topic"],
    verbs: {
      1: ["recall", "name"],
      2: ["distinguish", "explain"],
      3: ["calculate", "show"]
    },
    policy: "Vocational alignment required",
    regulator: "Regional Framework"
  },
  {
    name: "Middle East - CAA",
    intent: "Ensures compliance with accreditation standards in Gulf region institutions.",
    levels: ["Course", "Unit", "Topic"],
    verbs: {
      1: ["define", "identify"],
      2: ["summarize", "restate"],
      3: ["compute", "modify"]
    },
    policy: "Contact hours + self-study validation",
    regulator: "CAA"
  },
  {
  name: "Revised Bloom’s Taxonomy",
  intent: "Universal cognitive hierarchy guiding learning complexity.",
  levels: ["Remember", "Understand", "Apply", "Analyze", "Evaluate", "Create"],
  verbs: {
  1: ["define", "list", "recall", "identify", "name", "recognize", "state", "label", "match", "select"],
  2: ["explain", "summarize", "describe", "interpret", "classify", "paraphrase", "translate", "discuss", "illustrate", "outline"],
  3: ["apply", "calculate", "execute", "implement", "solve", "demonstrate", "use", "perform", "operate", "predict"],
  4: ["analyze", "differentiate", "organize", "examine", "categorize", "investigate", "distinguish", "infer", "relate", "test"],
  5: ["evaluate", "critique", "assess", "justify", "validate", "defend", "recommend", "judge", "appraise", "verify"],
  6: ["design", "create", "formulate", "construct", "develop", "synthesize", "generate", "propose", "build", "compose"]
},
  policy: "Cognitive scoring model | Verb-level alignment enforced",
  regulator: "Global Pedagogical Standard"
},
  {
    name: "BBRRIICCSS Model",
    intent: "Structured instructional flow used in Texila’s academic delivery.",
    levels: ["Module", "Unit", "Learning Outcome"],
    verbs: {
      1: ["identify", "state"],
      2: ["explain", "describe"],
      3: ["apply", "analyze", "create"]
    },
    policy: "No passive verbs | LO mapping required",
    regulator: "Texila Academic Model"
  }
];

const TaxonomyStandards = () => {
  const [selected, setSelected] = useState([]);

  const toggleSelect = (tax) => {
    if (selected.includes(tax)) {
      setSelected(selected.filter(t => t !== tax));
    } else {
      setSelected([...selected, tax]);
    }
  };

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">
      

      <Container className="py-5 flex-grow-1">
        <div className="text-center mb-5">
          <Badge bg="primary">AcademicOS Pedagogical Layer</Badge>
          <h1 className="fw-bold mt-2">Global Taxonomy Standards</h1>
          <p className="text-muted">
            Structured cognitive and hierarchical frameworks governing curriculum design and learning outcome articulation.
          </p>
        </div>

        <Accordion>
          {taxonomies.map((tax, idx) => (
            <Accordion.Item eventKey={idx.toString()} key={idx}>
              <Accordion.Header>
                <div className="d-flex justify-content-between w-100">
                  <span>{tax.name}</span>
                  <Button
                    size="sm"
                    variant={selected.includes(tax) ? "danger" : "outline-primary"}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(tax);
                    }}
                  >
                    {selected.includes(tax) ? "Remove" : "Compare"}
                  </Button>
                </div>
              </Accordion.Header>

              <Accordion.Body>
                <p><strong>Academic Intent:</strong> {tax.intent}</p>

                <h6 className="fw-bold mt-3">Hierarchy</h6>
                <ul>
                  {tax.levels.map((l, i) => <li key={i}>{l}</li>)}
                </ul>

                <h6 className="fw-bold mt-3">Cognitive Verbs</h6>
                <Table bordered size="sm">
                  <tbody>
                    {Object.entries(tax.verbs).map(([lvl, verbs]) => (
                      <tr key={lvl}>
                        <td style={{width: "20%"}}>Level {lvl}</td>
                          <td>
                            {verbs.map((v, i) => (
                              <Badge key={i} bg="secondary" className="me-1 mb-1">{v}</Badge>
                            ))}
                          </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Card className="mt-3 bg-light border-0">
                  <Card.Body>
                    <h6 className="fw-bold">Regulatory Traceability</h6>
                    <p><strong>Authority:</strong> {tax.regulator}</p>
                    <p><strong>Policy:</strong> {tax.policy}</p>
                  </Card.Body>
                </Card>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>

        {/* Comparison Mode */}
        {selected.length >= 2 && (
          <Card className="mt-5">
            <Card.Header className="bg-dark text-white">
              Taxonomy Comparison Mode
            </Card.Header>
            <Card.Body>
              <Table bordered>
                <thead>
                  <tr>
                    <th>Parameter</th>
                    {selected.map((t, i) => <th scope="col" key={i}>{t.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Hierarchy</td>
                    {selected.map((t, i) => (
                      <td key={i}>{t.levels.join(" → ")}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Policy</td>
                    {selected.map((t, i) => (
                      <td key={i}>{t.policy}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Regulator</td>
                    {selected.map((t, i) => (
                      <td key={i}>{t.regulator}</td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Container>

      <AppFooter />
    </div>
  );
};

export default TaxonomyStandards;
